import { useCallback, useMemo } from 'react';
import { useOpenModal } from '@rainbow-modules/hooks';
import { Llmtestinput } from 'data/firestore/agent/llmtestinput/types';
import { useParams } from 'react-router-dom';
import useHttpMutation from 'data/firestore/useHttpMutation';
import {
    confirmModal, hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import Trash from 'components/icons/trash';
import ModalBody from 'components/ModalBody';
import { JSONSchema } from 'data/firestore/globals';

const useManageLLMTestInputs = ({
    modalId,
    onInputGenerated = () => {},
}: {
    modalId: string;
    onInputGenerated?: (input: Record<string, unknown>) => void;
}) => {
    const { agentId = '' } = useParams();
    const [openModal, closeModal] = useOpenModal(modalId);

    const { mutateAsync: addNew } = useHttpMutation({
        method: 'POST',
        pathname: `/agents/${agentId}/llm-test-inputs`,
    });

    const { mutateAsync: remove } = useHttpMutation({
        method: 'DELETE',
    });

    const { mutateAsync: update } = useHttpMutation({
        method: 'PATCH',
    });

    const { mutateAsync: generate } = useHttpMutation({
        method: 'POST',
        pathname: `/agents/${agentId}/llm-test-inputs/generate`,
    });

    const handleSaveTestInput = useCallback(
        (data: Partial<Llmtestinput>) => new Promise((resolve) => {
            openModal({
                data,
                onSubmit: async (values: Record<string, unknown>) => {
                    const {
                        override, title, content, id,
                    } = values as Record<string, string>;
                    showAppSpinner();
                    let result;
                    try {
                        const shouldCreateNewInput = override === 'no' || (!override && !id);
                        if (shouldCreateNewInput) {
                            result = await addNew({
                                body: {
                                    title,
                                    content,
                                },
                            });
                        } else {
                            result = await update({
                                pathname: `/agents/${agentId}/llm-test-inputs/${id}`,
                                body: {
                                    title,
                                    content,
                                },
                            });
                        }
                        closeModal();
                    } catch (error) {
                        const message = (error as any)?.message || 'Something went wrong. Please try again.';
                        showAppNotification({
                            title: 'Error',
                            description: message,
                            icon: 'error',
                            timeout: 5000,
                        });
                    }
                    hideAppSpinner();
                    resolve(result);
                },
            });
        }),
        [addNew, agentId, closeModal, openModal, update],
    );

    const handleDelete = useCallback(
        (inputId: string) => new Promise<string>((resolve) => {
            (async () => {
                const result = await confirmModal({
                    header: '',
                    variant: 'destructive',
                    question: (
                        <ModalBody
                            variant="destructive"
                            icon={<Trash />}
                            title="Remove llm test input"
                            description="Once this action is confirmed, it cannot be undone."
                        />
                    ),
                    borderRadius: 'semi-square',
                    okButtonLabel: 'Remove',
                    cancelButtonLabel: 'Cancel',
                });

                if (!result) {
                    return;
                }

                showAppSpinner();
                try {
                    await remove({
                        pathname: `/agents/${agentId}/llm-test-inputs/${inputId}`,
                    });
                    resolve(inputId);
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
                hideAppSpinner();
            })();
        }),
        [remove, agentId],
    );

    const handleGenerateTestInput = useCallback(
        async ({
            inputSchema,
            input,
            customer,
        }: {
            inputSchema: JSONSchema;
            input?: Record<string, unknown>;
            customer?: Record<string, unknown>;
        }) => {
            showAppSpinner();
            try {
                const result = await generate({
                    body: {
                        inputSchema,
                        input,
                        customer,
                    },
                });
                onInputGenerated(result as Record<string, unknown>);
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
            hideAppSpinner();
        },
        [generate, onInputGenerated],
    );

    return useMemo(() => ({
        save: handleSaveTestInput,
        remove: handleDelete,
        generate: handleGenerateTestInput,
    }), [handleDelete, handleGenerateTestInput, handleSaveTestInput]);
};

export default useManageLLMTestInputs;
